import { StyledContainer } from "src/components/common/StyledComponents/Container";
import { StyledContainerInset } from "src/components/common/StyledComponents/BackgroundContainer";
import useResponsive from "src/hooks/useResponsive";
import { useEffect, useState } from "react";
import { adminService } from "src/services/services/admin/admin.api";
import { ResponseKind } from "src/services/services/MainService";
import { sendFailureNotification } from "src/utils/notifications";
import { NotificationTypes } from "src/enums/Notifications";
import { Subscription } from "src/interfaces";
import {
  Box,
  Grid,
  Table,
  Text,
  Button,
  Paper,
  Badge,
  TextInput,
  Select,
  Loader,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { formatDate } from "src/utils/helpers";
import { NOT_AVAILABLE_TEXT } from "src/utils/helpers";

export default function SubscriptionManagement() {
  const { backgroundColor, backgroundColorExterior } = useResponsive();
  const [subscription, setSubscription] = useState<Subscription[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getUserwithPaymentHistory();
  }, []);

  const getUserwithPaymentHistory = async () => {
    try {
      setLoading(true);
      const response = await adminService.getUserwithPaymentHistory();
      if (response.kind == ResponseKind.OK && response.data) {
        setSubscription(response.data);
      } else {
        throw "Error";
      }
    } catch (e) {
      sendFailureNotification(
        NotificationTypes.UserManagement,
        "Users could not be fetched"
      );
    } finally {
      setLoading(false);
    }
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const filteredSubscriptions = subscription.filter((sub) => {
    const matchesSearch =
      searchQuery === "" ||
      sub.user?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.user?.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.plan_name.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      !statusFilter ||
      (statusFilter === "active" && sub.status === "active") ||
      (statusFilter === "cancelled" && sub.status !== "active");

    return matchesSearch && matchesStatus;
  });

  return (
    <StyledContainer theme={{ backgroundColor: backgroundColorExterior }}>
      <StyledContainerInset theme={{ backgroundColor: backgroundColor }}>
        <div className="p-4 h-[100%] overflow-y-auto">
          <Text size="xl" weight={700} mb="md">
            Subscription Management
          </Text>
          <Grid mb="md">
            <Grid.Col span={8}>
              <TextInput
                placeholder="Search by name, email or plan..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <Select
                placeholder="Filter by status"
                value={statusFilter}
                onChange={setStatusFilter}
                data={[
                  { value: "", label: "All" },
                  { value: "active", label: "Active" },
                  { value: "cancelled", label: "Cancelled" },
                ]}
              />
            </Grid.Col>
          </Grid>
          <Box sx={{ maxHeight: "80vh", width: "80%", overflow: "auto" }}>
            {loading ? (
              <div className="flex justify-center items-center h-full">
                <Loader size="md" />
              </div>
            ) : filteredSubscriptions.length === 0 ? (
              <Paper p="xl" withBorder>
                <Box sx={{ textAlign: "center" }}>
                  <Text size="lg" weight={500} color="dimmed">
                    No payment history found
                  </Text>
                  <Text size="sm" color="dimmed" mt="xs">
                    This user has not made any payments yet.
                  </Text>
                </Box>
              </Paper>
            ) : (
              filteredSubscriptions.map((sub) => (
                <Paper
                  key={sub.stripe_subscription_id}
                  p="md"
                  mb="md"
                  withBorder
                  mr={"md"}
                >
                  <Box mb="md">
                    <Grid>
                      <Grid.Col span={6}>
                        <Text>
                          <b>Customer ID:</b> {sub.customer_id}
                        </Text>
                        <Text>
                          <b>User Name:</b>{" "}
                          {sub.user?.name || NOT_AVAILABLE_TEXT}
                        </Text>
                        <Text>
                          <b>User Email:</b>{" "}
                          {sub.user?.email || NOT_AVAILABLE_TEXT}
                        </Text>
                        <Text>
                          <b>Plan:</b> {sub.plan_name}
                        </Text>
                        <Text>
                          <b>Status:</b>
                          {sub.status == "active" ? (
                            <Badge color="green">Active</Badge>
                          ) : (
                            <Badge color="red">Inactive</Badge>
                          )}
                        </Text>
                        <Text>
                          <b>Start Date:</b> {formatDate(sub.start_date)}
                        </Text>
                        <Text>
                          <b>End Date:</b> {formatDate(sub.end_date)}
                        </Text>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Text>
                          <b>Original Price:</b>{" "}
                          {formatCurrency(sub.original_price)}
                        </Text>
                        <Text>
                          <b>Discount:</b> {formatCurrency(sub.discount_amount)}
                        </Text>
                        <Text>
                          <b>Final Price:</b> {formatCurrency(sub.final_price)}
                        </Text>
                        <Text>
                          <b>Discount Code:</b> {sub.discount_code || "None"}
                        </Text>
                        <Text>
                          <b>Last Payment:</b>{" "}
                          {formatDate(sub.last_payment_date)}
                        </Text>
                      </Grid.Col>
                    </Grid>
                  </Box>

                  {sub.invoices && sub.invoices.length > 0 && (
                    <Box>
                      <Text size="lg" weight={600} mb="sm">
                        Invoices
                      </Text>
                      <Table striped highlightOnHover>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Invoice</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sub.invoices.map((invoice) => (
                            <tr key={invoice.invoice_id}>
                              <td>{formatDate(invoice.created_at)}</td>
                              <td>{formatCurrency(invoice.amount)}</td>
                              <td>{invoice.status}</td>
                              <td>
                                {invoice.invoice_pdf_url && (
                                  <Button
                                    variant="outline"
                                    size="xs"
                                    onClick={() =>
                                      window.open(
                                        invoice.invoice_pdf_url,
                                        "_blank"
                                      )
                                    }
                                  >
                                    View PDF
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Box>
                  )}
                </Paper>
              ))
            )}
          </Box>
        </div>
      </StyledContainerInset>
    </StyledContainer>
  );
}
